import { computed, ref } from 'vue'
import { injectBackendClient } from '~/api/backendClients'
import { injectServerClient } from '~/api/serverClient'
import useFetchData from '~/composables/common/useFetchData'
import { useI18n } from 'vue-i18n'
import type { VersionResponse } from '@gridcon-energy/schaltungstool-backend'

type UseAppVersionInfoOptions = {
  onError?: (error: Error) => any
}

type VersionInfoItem = {
  label: string
  version: string
  commit?: {
    long: string
    short: string
    url: string | null
  }
}

export function useAppVersionInfo(options?: UseAppVersionInfoOptions) {
  const server = injectServerClient()
  const backendClient = injectBackendClient()

  const frontend = ref<null | VersionResponse>(null)
  const backend = ref<null | VersionResponse>(null)
  const { t } = useI18n()

  const frontendVersionFetch = useFetchData(
    async function fetchFrontendVersion() {
      frontend.value = await server.frontendVersion()
    },
    {
      onError: options?.onError,
    },
  )

  const backendVersionFetch = useFetchData(
    async function fetchBackendVersion() {
      const response = await backendClient.app.versionInfo()
      backend.value = response.data
    },
    {
      onError: options?.onError,
    },
  )

  const pending = computed(() => frontendVersionFetch.state.pending || backendVersionFetch.state.pending)
  const copyContent = computed(() =>
    [
      formatVersionCopyLine('Frontend', frontend.value),
      formatVersionCopyLine('Backend', backend.value),
    ].join('\n'),
  )

  async function ensureVersionsLoaded() {
    await Promise.all([
      frontend.value ? Promise.resolve() : frontendVersionFetch(),
      backend.value ? Promise.resolve() : backendVersionFetch(),
    ])
  }

  const versionInfoItems = computed(() => {
    const items: VersionInfoItem[] = []

    if (frontend.value?.app) {
      items.push({
        label: t('AppFrontend'),
        version: frontend.value.app,
        commit: frontend.value.commit ?? undefined,
      })
    }

    if (backend.value?.app) {
      items.push({
        label: t('AppBackend'),
        version: backend.value.app,
        commit: backend.value.commit ?? undefined,
      })
    }
    return items
  })

  return {
    items: versionInfoItems,
    pending: pending,
    ensureVersionsLoaded: ensureVersionsLoaded,
    copyContent: copyContent,
  }
}

function formatVersionCopyLine(label: string, versionInfo: null | VersionResponse): string {
  const version = versionInfo?.app ?? 'Undefined'
  const commit = versionInfo?.commit?.short ?? null

  return commit ? `${label}: ${version} (${commit})` : `${label}: ${version}`
}
